import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CrmDashboardSummary,
  CrmDeal,
  CrmForecast,
  CrmLeadRequest,
  CrmLeadResponse,
  CrmPipeline,
  CrmCompany,
  Customer360,
  CrmEmailAccount,
  CrmEmailAccountRequest,
} from '@/types/crm';

export const crmApi = {
  listLeads: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmLeadResponse>>>('/v1/crm/leads', { params })),

  createLead: (body: CrmLeadRequest) =>
    unwrap(api.post<ApiResponse<CrmLeadResponse>>('/v1/crm/leads', body)),

  getLead: (id: number) =>
    unwrap(api.get<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}`)),

  updateLead: (id: number, body: CrmLeadRequest) =>
    unwrap(api.put<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}`, body)),

  deleteLead: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/crm/leads/${id}`)),

  convertLead: (id: number, body: import('@/types/crm').CrmConvertLeadRequest) =>
    unwrap(api.post<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}/convert`, body)),

  bulkCreateLeads: (leads: CrmLeadRequest[]) =>
    unwrap(api.post<ApiResponse<{ successCount: number; failureCount: number }>>('/v1/crm/leads/bulk', { leads })),

  dashboardSummary: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'MONTH') =>
    unwrap(api.get<ApiResponse<CrmDashboardSummary>>('/v1/crm/dashboard/summary', { params: { period } })),

  listPipelines: () => unwrap(api.get<ApiResponse<CrmPipeline[]>>('/v1/crm/pipelines')),

  ensureDefaultPipeline: () =>
    unwrap(api.post<ApiResponse<CrmPipeline>>('/v1/crm/pipelines/ensure-default')),

  listCompanies: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmCompany>>>('/v1/crm/companies', { params })),

  createCompany: (body: Partial<CrmCompany>) =>
    unwrap(api.post<ApiResponse<CrmCompany>>('/v1/crm/companies', body)),

  listDeals: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmDeal>>>('/v1/crm/deals', { params })),

  createDeal: (body: Partial<CrmDeal>) =>
    unwrap(api.post<ApiResponse<CrmDeal>>('/v1/crm/deals', body)),

  moveDealStage: (dealId: number, stageId: number) =>
    unwrap(api.post<ApiResponse<CrmDeal>>(`/v1/crm/deals/${dealId}/stages/${stageId}`)),

  approveDealWin: (dealId: number) =>
    unwrap(api.post<ApiResponse<CrmDeal>>(`/v1/crm/deals/${dealId}/approve-win`)),

  customer360: (customerId: number) =>
    unwrap(api.get<ApiResponse<Customer360>>(`/v1/crm/customers/${customerId}/360`)),

  forecast: () => unwrap(api.get<ApiResponse<CrmForecast>>('/v1/crm/forecast')),

  reports: () => unwrap(api.get<ApiResponse<import('@/types/crm').CrmReportsBundle>>('/v1/crm/reports')),

  listCampaigns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<import('@/types/crm').CrmCampaign>>>('/v1/crm/campaigns', { params })),

  createCampaign: (body: Partial<import('@/types/crm').CrmCampaign> & { name: string; channel: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmCampaign>>('/v1/crm/campaigns', body)),

  launchCampaign: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmCampaign>>(`/v1/crm/campaigns/${id}/launch`)),

  scheduleCampaign: (id: number, scheduledAt: string) =>
    unwrap(
      api.post<ApiResponse<import('@/types/crm').CrmCampaign>>(`/v1/crm/campaigns/${id}/schedule`, null, {
        params: { scheduledAt },
      }),
    ),

  createDealQuote: (dealId: number) =>
    unwrap(api.post<ApiResponse<number>>(`/v1/crm/deals/${dealId}/quote`)),

  listOpenTasks: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmTask[]>>('/v1/crm/tasks')),

  createTask: (body: {
    title: string;
    description?: string;
    customerId?: number;
    dealId?: number;
    leadId?: number;
    dueDate?: string;
    priority?: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/crm').CrmTask>>('/v1/crm/tasks', body)),

  completeTask: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmTask>>(`/v1/crm/tasks/${id}/complete`)),

  // Setup
  listScoringRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmScoringRule[]>>('/v1/crm/setup/scoring-rules')),
  createScoringRule: (body: Omit<import('@/types/crm').CrmScoringRule, 'id'>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmScoringRule>>('/v1/crm/setup/scoring-rules', body)),
  listAssignmentRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmAssignmentRule[]>>('/v1/crm/setup/assignment-rules')),
  listWorkflows: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmWorkflow[]>>('/v1/crm/setup/workflows')),
  listLeadSources: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmLeadSource[]>>('/v1/crm/setup/lead-sources')),
  createLeadSource: (body: { code: string; name: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmLeadSource>>('/v1/crm/setup/lead-sources', body)),
  listTerritories: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmTerritory[]>>('/v1/crm/setup/territories')),

  listEmailAccounts: () =>
    unwrap(api.get<ApiResponse<CrmEmailAccount[]>>('/v1/crm/setup/email-accounts')),

  createEmailAccount: (body: CrmEmailAccountRequest) =>
    unwrap(api.post<ApiResponse<CrmEmailAccount>>('/v1/crm/setup/email-accounts', body)),

  syncEmailAccounts: () =>
    unwrap(api.post<ApiResponse<number>>('/v1/crm/setup/email-accounts/sync')),

  googleOAuthAuthorizeUrl: () =>
    unwrap(api.get<ApiResponse<{ url: string; state: string }>>('/v1/crm/setup/email-accounts/oauth/google/authorize-url')),

  connectGoogleOAuth: (code: string, emailAddress?: string) =>
    unwrap(
      api.post<ApiResponse<CrmEmailAccount>>('/v1/crm/setup/email-accounts/oauth/google/connect', {
        code,
        emailAddress,
      }),
    ),

  listCustomerActivities: (customerId: number, limit = 50) =>
    unwrap(
      api.get<ApiResponse<import('@/types/crm').CrmActivityTimelineItem[]>>(
        `/v1/crm/customers/${customerId}/activities`,
        { params: { limit } }
      )
    ),
  createActivity: (body: {
    customerId?: number;
    leadId?: number;
    dealId?: number;
    activityType: string;
    title: string;
    description?: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/crm').CrmActivityTimelineItem>>('/v1/crm/activities', body)),
};
